import React from "react";

/**
 * MarketHeader
 * ----------------------------------------------------------------------------
 * Reference-style header strip shown above each market table. Layout:
 *
 *   [ Market Name ]  (i)   ● Cash Out            Matched € 28.7M
 *   [ Min/Max  100-100000 ]
 *
 * Purely presentational — parent passes the label, matched amount and
 * min/max. The "Cash Out" badge and (i) are static visuals (match the
 * reference); hook up onCashOut / onInfo if/when needed.
 *
 * Props:
 *   name       - market name (e.g. "Match Odds")
 *   matched    - matched amount string/number (e.g. "28.7M" or 28700000)
 *   min, max   - bet limits for the Min/Max row
 *   currency   - symbol shown before matched amount (default "€")
 *   showMinMax - show the Min/Max row (default true)
 *   onInfo     - optional click handler for the (i) icon
 *   onCashOut  - optional click handler for the Cash Out badge
 */
const MarketHeader = ({
  name = "Match Odds",
  matched,
  min,
  max,
  currency = "€",
  showMinMax = true,
  showMatched = true,
  onInfo,
  onCashOut,
}) => {
  const longName = name.length > 30;
  return (
    <>
      <style>{mhStyles}</style>
      <div className="mh-wrap">
        <div className="mh-top" style={longName ? { display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", height: "auto", minHeight: "30px" } : undefined}>
          <span className="mh-name" style={longName ? { minHeight: "30px", height: "100%", maxWidth: "100%", boxSizing: "border-box", whiteSpace: "normal", fontSize: "12px" } : undefined}>
            <span style={{ minWidth: 0 }}>{name}</span>
            <button type="button" className="mh-info" onClick={onInfo} aria-label={`${name} limits`}>
              <svg width="15" height="15" viewBox="0 0 20 20" aria-hidden="true">
                <circle cx="10" cy="10" r="8.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
                <path fill="currentColor" d="M9 5h2v2H9zm0 4h2v6H9z" />
              </svg>
            </button>
          </span>
          <a
            href="javascript:void(0)"
            className="mh-cashout"
            style={longName ? { whiteSpace: "nowrap", paddingRight: "10px" } : undefined}
            onClick={onCashOut}
          >
            <span className="mh-dot" />
            Cash Out
          </a>
          {showMatched && (
            <span className="mh-matched" style={longName ? { gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "3px", minHeight: "28px", marginLeft: 0 } : undefined}>
              Matched {matched == null ? <strong>—</strong> : <>{currency} <strong>{matched}</strong></>}
            </span>
          )}
        </div>

        {showMinMax && (
          <div className="mh-minmax">
            <span className="mh-minmax-label">Min/Max</span>
            <span className="mh-minmax-val">
              {min}-{max}
            </span>
          </div>
        )}
      </div>
    </>
  );
};

const mhStyles = `
.mh-wrap{ background:#fff; font-family:Tahoma,Helvetica,sans-serif; border-bottom:1px solid #7e97a7; }
.mh-top{
  display:flex;
  align-items:center;
  background:#fff;
  padding:0;
  height:30px;
  min-width:0;
  white-space:nowrap;
}
.mh-name{
  display:flex;
  align-items:center;
  gap:12px;
  min-width:0;
  flex:0 1 auto;
  background:#263e4e;
  color:#fff;
  font-size:13px;
  font-weight:700;
  height:100%;
  padding:0 10px;
  /* only the TOP-right corner is rounded; bottom edge stays straight */
  border-top-right-radius:20px;
  border-bottom-right-radius:0;
  padding-right:10px;
}
/* (i) info icon sits INSIDE the dark Match Odds tab. Strip the theme's
   box (padding/height/background) and draw a plain white info glyph. */
.mh-info{
  display:inline-block;
  width:15px;
  height:15px;
  color:#fff;
  line-height:1;
  box-shadow:none;
  cursor:pointer;
  min-width:0;
  flex:0 0 auto;
  padding:0;
  margin:0;
  background:transparent;
  border:none;
}
/* kill the theme's ::before glyph so there's no double icon/box */
.mh-info::before{ display:none; content:none; }
.mh-cashout{
  display:flex;
  align-items:center;
  gap:5px;
  margin-left:7px;
  color:#1a1a1a;
  font-size:12px;
  font-weight:700;
  text-decoration:none;
}
/* yellow rounded square box with a black circle inside (reference icon) */
.mh-dot{
  display:flex;
  align-items:center;
  justify-content:center;
  width:18px;
  height:18px;
  border-radius:4px;
  background:#feb902;
  flex:0 0 auto;
}
.mh-dot::before{
  content:"";
  width:10px;
  height:10px;
  border-radius:50%;
  background:#111;
}
.mh-matched{
  margin-left:auto;
  padding:0 10px 0 6px;
  color:#111;
  font-size:12px;
  font-weight:400;
}
.mh-matched strong{ color:#1a1a1a; font-weight:700; }
.mh-minmax{
  display:flex;
  align-items:center;
  gap:2.4vw;
  background:#dfe7ea;
  padding:1.4vw 3.2vw;
  font-size:3.2vw;
}
.mh-minmax-label{ color:#2b6fb0; font-weight:700; }
.mh-minmax-val{ color:#1a1a1a; font-weight:700; }
`;

export const marketRowStyles = {
  row: {
    display: "grid", gridTemplateColumns: "minmax(0, 1fr) repeat(2, 18%)",
    position: "relative", width: "100%", minWidth: 0, minHeight: "42px",
    height: "auto", margin: 0, padding: 0, boxSizing: "border-box",
    background: "#fff", borderBottom: "1px solid #7e97a7",
    fontFamily: "Tahoma, Helvetica, sans-serif", lineHeight: 1.2,
  },
  selection: {
    display: "flex", flexDirection: "column", justifyContent: "center",
    alignItems: "flex-start", minWidth: 0, width: "100%", margin: 0,
    padding: "4px 6px", boxSizing: "border-box", textAlign: "left",
  },
  name: {
    display: "block", width: "100%", margin: 0, padding: 0, color: "#252a2d",
    background: "transparent", fontSize: "13px", fontWeight: 700,
    lineHeight: 1.2, whiteSpace: "normal", overflowWrap: "anywhere",
  },
  positions: {
    display: "flex", flexWrap: "wrap", alignItems: "center", gap: "5px",
    width: "100%", padding: 0, margin: "4px 0 0", listStyle: "none",
    fontSize: "12px", fontWeight: 400, lineHeight: 1.2,
  },
  headerCell: {
    display: "flex", alignItems: "center", justifyContent: "center", width: "100%",
    minWidth: 0, minHeight: 0, margin: 0, padding: 0, border: 0,
    borderLeft: "1px solid #fff", boxSizing: "border-box",
    fontSize: "12px", fontWeight: 700, color: "#111", lineHeight: 1,
  },
  suspension: {
    position: "absolute", top: 0, right: 0, bottom: 0, width: "36%", height: "100%",
    display: "flex", alignItems: "center", justifyContent: "center", margin: 0,
    padding: 0, zIndex: 9, background: "rgba(42, 52, 60, .55)", color: "#fff",
    fontSize: "13px", fontWeight: 700, textAlign: "center", boxSizing: "border-box",
  },
};

export const MarketLimits = ({ min, max, bookmaker = false, depth = 1 }) => {
  // Desktop full-ladder header: 3 Back cells + 3 Lay cells. The Back/Lay label
  // sits on the innermost cell (adjacent to the center) where the best price —
  // and the only clickable bet cell — lives.
  if (depth === 3) {
    // Desktop full-ladder header. Only the two innermost cells (best back /
    // best lay) carry a strong colour + the Back/Lay label — matching the
    // reference. The two outer back cells are light blue, and the two outer
    // lay cells host the Min/Max box.
    return (
      <dl style={{ ...marketRowStyles.row, minHeight: "32px", gridTemplateColumns: "minmax(0, 1fr) repeat(6, 9%)" }}>
        <dt style={{ ...marketRowStyles.selection, padding: "2px 5px", background: "#fff" }} />
        <dd style={{ ...marketRowStyles.headerCell, background: "#fff", borderLeft: 0 }} />
        <dd style={{ ...marketRowStyles.headerCell, background: "#fff", borderLeft: 0 }} />
        <dd style={{ ...marketRowStyles.headerCell, background: "#72bbef", borderTopLeftRadius: "10px" }}>Back</dd>
        <dd style={{ ...marketRowStyles.headerCell, background: "#faa9ba", borderTopRightRadius: "10px" }}>Lay</dd>
        <dd style={{ ...marketRowStyles.headerCell, background: "#fff", borderLeft: 0, gridColumn: "6 / 8", padding: "0 4px" }}>
          <span style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "6px", width: "100%", minHeight: "20px", background: "#cfdbe0", borderRadius: "3px", fontSize: "11px", fontWeight: 700, color: "#111" }}>
            <span style={{ color: "#305b9b" }}>Min/Max</span>
            <span>{min == null && max == null ? "—" : `${min ?? "—"}-${max ?? "—"}`}</span>
          </span>
        </dd>
      </dl>
    );
  }
  return (
    <dl style={{ ...marketRowStyles.row, minHeight: "32px", gridTemplateColumns: `minmax(0, 1fr) repeat(2, ${bookmaker ? "17.5%" : "18%"})` }}>
      <dt style={{ ...marketRowStyles.selection, padding: "2px 5px" }}>
        <span style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", width: "100%", minHeight: "20px", background: bookmaker ? "#bed5d8" : "#cfdbe0", borderRadius: "3px", fontSize: "11px", fontWeight: 700, color: "#111" }}>
          <span style={{ color: "#305b9b" }}>Min/Max</span>
          <span>{min == null && max == null ? "—" : `${min ?? "—"}-${max ?? "—"}`}</span>
        </span>
      </dt>
      <dd style={{ ...marketRowStyles.headerCell, background: bookmaker ? "linear-gradient(90deg, #f8f6df, #8fbdd8)" : "#72bbef" }}>Back</dd>
      <dd style={{ ...marketRowStyles.headerCell, background: "#faa9ba" }}>Lay</dd>
    </dl>
  );
};

export const MarketSeparator = ({ height = 16 }) => (
  <div aria-hidden="true" style={{ height, width: "100%", margin: 0, background: "#eee", borderTop: "1px solid #bfcad1", borderBottom: "1px solid #bfcad1", boxSizing: "border-box" }} />
);

// Display-only fallback: no selection IDs, odds handlers, or betslip controls.
export const SuspendedMarket = ({ name, runners = [], min, max, bookmaker = false, showHeader = true, marginTop = 16, statusLabel = "SUSPENDED" }) => {
  const names = runners.map((runner) =>
    typeof runner === "string" ? runner : runner?.RunnerName || runner?.runnerName
  ).filter(Boolean);

  return (
    <section aria-label={`${name} unavailable`} style={{ margin: 0, background: "#fff" }}>
      {marginTop > 0 && <MarketSeparator height={marginTop} />}
      {showHeader && <MarketHeader name={name} showMinMax={false} showMatched={false} />}
      <div
        aria-disabled="true"
        style={{ position: "relative", border: "1px solid #e24949", boxSizing: "border-box", pointerEvents: "none" }}
      >
        <div aria-hidden="true" style={{ opacity: 0.22 }}>
          <MarketLimits min={min} max={max} bookmaker={bookmaker} />
          {(names.length ? names : ["—", "—"]).map((runner, index) => (
            <dl key={`${runner}-${index}`} style={{ ...marketRowStyles.row, gridTemplateColumns: `minmax(0, 1fr) repeat(2, ${bookmaker ? "17.5%" : "18%"})`, background: bookmaker ? "#faf9d9" : "#fff" }}>
              <dt style={marketRowStyles.selection}>
                <span style={marketRowStyles.name}>{runner}</span>
              </dt>
              {["#72bbef", "#faa9ba"].map((background) => (
                <dd key={background} style={{ ...marketRowStyles.headerCell, flexDirection: "column", gap: "3px", background }}>
                  <span>—</span>
                  <span style={{ fontSize: "11px", fontWeight: 400 }}>—</span>
                </dd>
              ))}
            </dl>
          ))}
        </div>
        <div
          role="status"
          style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#df4545", fontFamily: "Tahoma, Helvetica, sans-serif", fontSize: "30px", fontWeight: 700, lineHeight: 1.2 }}
        >
          {statusLabel}
        </div>
      </div>
    </section>
  );
};

export const formatMarketAmount = (value) => value == null || !Number.isFinite(Number(value))
  ? "—" : Number(value).toLocaleString("en-US", { notation: "compact", maximumFractionDigits: 1 });

const activeQuoteStatus = (status) => ["1", "OPEN", "ACTIVE", "ONLINE"].includes(String(status).toUpperCase());
const quoteStatusLabel = (status) => ({ CLOSED: "CLOSED", 4: "CLOSED", BALL_RUNNING: "BALL RUNNING", 9: "BALL RUNNING", INACTIVE: "INACTIVE", 2: "INACTIVE" }[String(status).toUpperCase()] || "SUSPENDED");
const firstQuote = (ladder = []) => ladder.find((quote) => Number.isFinite(Number(quote?.price)) && Number(quote.price) > 0);

// Feed quotes are displayed independently of the existing bet-placement flow.
export const MarketQuotePanel = ({ market, blocked = false }) => {
  const runners = market.jsonData || [];
  const rowOpen = (runner) => activeQuoteStatus(runner.status) && Boolean(firstQuote(runner.availableToBack) || firstQuote(runner.availableToLay));
  if (blocked || !activeQuoteStatus(market.status) || !runners.some(rowOpen)) {
    return <SuspendedMarket name={market.market} runners={runners} min={market.min} max={market.max} bookmaker statusLabel={blocked ? "SUSPENDED" : quoteStatusLabel(market.status)} />;
  }
  return (
    <section aria-label={market.market}>
      <MarketSeparator />
      <MarketHeader name={market.market} matched={market.totalMatched == null ? undefined : formatMarketAmount(market.totalMatched)} showMinMax={false} />
      <MarketLimits min={market.min} max={market.max} bookmaker />
      {runners.map((runner) => (
        <dl key={runner.selectionId} style={{ ...marketRowStyles.row, gridTemplateColumns: "minmax(0, 1fr) repeat(2, 17.5%)", background: "#faf9d9" }}>
          <dt style={marketRowStyles.selection}>
            <span style={marketRowStyles.name}>{runner.runnerName}</span>
          </dt>
          {[runner.availableToBack, runner.availableToLay].map((ladder, index) => {
            const quote = rowOpen(runner) ? firstQuote(ladder) : undefined;
            return (
              <dd key={index} style={{ display: "flex", minWidth: 0, minHeight: "41px", margin: 0, padding: "3px", boxSizing: "border-box", background: index === 0 ? "#c4dce8" : "#efd0d6" }}>
                <div style={{ ...marketRowStyles.headerCell, flexDirection: "column", gap: "3px", padding: "3px 1px", border: "1px solid #fff", borderRadius: "3px", background: index === 0 ? "#72bbef" : "#faa9ba", fontSize: "13px" }}>
                  <span>{quote?.price ?? "—"}</span>
                  <span style={{ fontSize: "11px", fontWeight: 400 }}>{formatMarketAmount(quote?.size)}</span>
                </div>
              </dd>
            );
          })}
          {!rowOpen(runner) && <dd style={{ ...marketRowStyles.suspension, width: "35%" }}>{quoteStatusLabel(runner.status)}</dd>}
        </dl>
      ))}
    </section>
  );
};

export default MarketHeader;
